import React, { useState } from "react";
import * as styles from "./ComponentTabularDataWithAccordion.module.scss";
import { IoMdCheckmark, IoMdInformation } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import { FiPlus, FiMinus } from "react-icons/fi";
import { RichText } from "../RichText/RichText";
import PlanConsultationModal from "../PlanConsultationModal/PlanConsultationModal";

type Props = {
    data: any;
};

const ComponentTabularDataWithAccordion: React.FC<Props> = ({ data }) => {
    if (!data) return null;

    const {
        title,
        tag,
        description,
        planTitles = [],
        highlightPlanIndex = 0,
        featureGroups = [],
        bottomButtons = [],
    } = data;

    const [open, setOpen] = useState<Record<number, boolean>>({});
    const [selectedPlanName, setSelectedPlanName] = useState("");
    const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);

    const toggle = (i: number) =>
        setOpen(prev => ({ ...prev, [i]: !prev[i] }));

    const renderCell = (v: boolean) =>
        v ? <IoMdCheckmark /> : <RxCross2 />;

    const gridCols = `2fr repeat(${planTitles.length}, 1fr)`;

    const openConsultationModal = (planName: string) => {
        setSelectedPlanName(planName || "Consulting Request");
        setIsConsultationModalOpen(true);
    };

    return (
        <section className={styles.section}>
            <div className={`container c-padding`}>

                <div className={styles.tableScroll}>

                    <div className={styles.wrapper}>
                        {tag && <p className="global-spacing-bottom">{tag}</p>}
                        {title && (
                            <h1 className="global-spacing-bottom">{title}</h1>
                        )}
                        {description && (
                            <div className="global-spacing-bottom">
                                <RichText content={description} />
                            </div>
                        )}
                        <div
                            className={styles.row}
                            style={{ gridTemplateColumns: gridCols }}
                        >
                            <h3 className={`${styles.cell} ${styles.headerMain}`}>
                                Service
                            </h3>
                            {planTitles.map((plan: string, i: number) => (
                                <h3
                                    key={i}
                                    className={`${styles.cell} ${styles.headerMain} ${i === highlightPlanIndex ? styles.highlight : ""
                                        }`}
                                >
                                    {plan}
                                </h3>
                            ))}
                        </div>
                        {featureGroups.map((group: any, gi: number) => {
                            const isOpen = open[gi] ?? group.defaultOpen;

                            return (
                                <div key={gi}>
                                    <div
                                        className={`${styles.row} ${styles.groupRow}`}
                                        style={{ gridTemplateColumns: gridCols }}
                                    >
                                        <div
                                            className={styles.groupLabel}
                                            onClick={() => toggle(gi)}
                                        >
                                            {isOpen ? <FiMinus /> : <FiPlus />}

                                            <div className={styles.groupLabelTitle}>
                                                {group.groupTitle}
                                            </div>

                                            {group.infoTooltip && (
                                                <span className={styles.infoWrap}>
                                                    <IoMdInformation />
                                                    <div className={styles.tooltip}>
                                                        <RichText content={group.infoTooltip} />
                                                    </div>
                                                </span>
                                            )}
                                        </div>

                                        {group.planDescriptions?.map(
                                            (text: string, i: number) => (
                                                <div
                                                    key={i}
                                                    className={`${styles.cell} ${styles.groupPlanText
                                                        } ${i === highlightPlanIndex
                                                            ? styles.highlight
                                                            : ""
                                                        }`}
                                                >
                                                    {text}
                                                </div>
                                            )
                                        )}
                                    </div>

                                    {isOpen &&
                                        group.rows?.map((row: any, ri: number) => (
                                            <div
                                                key={ri}
                                                className={styles.row}
                                                style={{ gridTemplateColumns: gridCols }}
                                            >
                                                <div className={styles.featureLabel}>
                                                    {row.label}
                                                </div>

                                                {(row.values || []).map(
                                                    (valObj: any, ci: number) => {
                                                        const value = Boolean(valObj?.content);

                                                        return (
                                                            <div
                                                                key={ci}
                                                                className={`${styles.cell} ${ci === highlightPlanIndex
                                                                        ? styles.highlight
                                                                        : ""
                                                                    }`}
                                                            >
                                                                {renderCell(value)}
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        ))}
                                </div>
                            );
                        })}
                        {/* Bottom CTA Row */}
                        {bottomButtons?.length > 0 && (
                            <div
                                className={`global-spacing ${styles.bottomButtonsRow}`}
                                style={{ gridTemplateColumns: gridCols }}
                            >
                                {/* Empty first column */}
                                <div />

                                {planTitles.map((_: string, i: number) => {
                                    const isLastColumn = i === planTitles.length - 1;

                                    return (
                                        <div key={i} className={styles.buttonCell}>
                                            {isLastColumn && bottomButtons?.[i] && (
                                                <button
                                                    type="button"
                                                    className={`primaryFilled ${styles.planButton} ${
                                                        i === highlightPlanIndex
                                                            ? styles.highlight
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        openConsultationModal(
                                                            bottomButtons[i].title || planTitles[i]
                                                        )
                                                    }
                                                >
                                                    {bottomButtons[i].title}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                <PlanConsultationModal
                    isOpen={isConsultationModalOpen}
                    onClose={() => setIsConsultationModalOpen(false)}
                    planName={selectedPlanName}
                    modalContent={data.consultationModalContent}
                />
            </div>
        </section>
    );
};

export default ComponentTabularDataWithAccordion;
